import React from 'react';
import { Table } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';
import { LomavisTableTitle1 } from '@/lomavisSharedUi/atoms/typography/tables/LomavisTableTitle1';
import { LomavisGrowthBadge2 } from '@/lomavisSharedUi/atoms/badges/LomavisGrowthBadge2';

interface ColumnConfig {
  key: string;
  label: string;
  align?: 'start' | 'end' | 'center';
  showGrowthArrow?: boolean;
  blurGrowthIndicator?: boolean;
  isNumeric?: boolean;
  showPercentage?: boolean;
  showAbbreviation?: boolean;
}

interface GrowthValue {
  current: number;
  previous: number;
}

type CellPrimitive = string | number;
type RowData = Record<string, CellPrimitive | GrowthValue>;

export interface LomavisTableWithSum1Props {
  title: string;
  columns: ColumnConfig[];
  rows: RowData[];
  showSumRow?: boolean;
}

const isGrowthObject = (val: unknown): val is GrowthValue =>
  typeof val === 'object' &&
  val !== null &&
  'current' in val &&
  'previous' in val &&
  typeof (val as any).current === 'number' &&
  typeof (val as any).previous === 'number';

export const LomavisTableWithSum1: React.FC<LomavisTableWithSum1Props> = ({
  title,
  columns,
  rows,
  showSumRow = true
}) => {
  const t = translateComponent('LomavisTableWithSum1');

  // Check if a specific column has growth data shown in any row
  const hasColumnGrowthData = (columnKey: string): boolean => {
    for (const row of rows) {
      const growthKey = `growth_${columnKey}`;
      const growthVal = row[growthKey];
      if (isGrowthObject(growthVal)) {
        return true;
      }
    }
    return false;
  };

  const calculateSum = (key: string, isPercentage: boolean = false): number => {
    const sum = rows.reduce((sum, row) => {
      const val = row[key];
      return typeof val === 'number' ? sum + val : sum;
    }, 0);

    // If it's a percentage column, return the average instead of sum
    return isPercentage && rows.length > 0 ? sum / rows.length : sum;
  };

  return (
    <div>
      {title && (
        <div className="mb-2">
          <LomavisTableTitle1>{title}</LomavisTableTitle1>
        </div>
      )}
      <Table
        hover
        responsive
        className="align-middle mb-0"
        style={{ width: '100%', tableLayout: 'auto' }}
      >
        <thead>
          <tr className="fw-bolder fs-3">
            {columns.map((col, colIndex) => {
              // Add padding to first and last cells
              const isFirstCell = colIndex === 0;
              const isLastCell = colIndex === columns.length - 1;
              const paddingStyle = {
                minWidth: col.isNumeric ? '120px' : 'auto',
                paddingLeft: isFirstCell ? '1rem' : undefined,
                paddingRight: isLastCell ? '1rem' : undefined
              };

              return (
                <th
                  key={col.key}
                  className={`text-${col.align ?? 'start'}`}
                  style={paddingStyle}
                >
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-top">
              {columns.map((col, colIndex) => {
                const raw = row[col.key];
                const currentValue = typeof raw === 'object' ? '' : raw;

                const growthKey = `growth_${col.key}`;
                const growthVal = row[growthKey];

                // Add padding to first and last cells
                const isFirstCell = colIndex === 0;
                const isLastCell = colIndex === columns.length - 1;
                const paddingStyle = {
                  minWidth: col.isNumeric ? '120px' : 'auto',
                  paddingLeft: isFirstCell ? '1rem' : undefined,
                  paddingRight: isLastCell ? '1rem' : undefined
                };

                return (
                  <td
                    key={col.key}
                    className={`text-${
                      col.align ?? (col.isNumeric ? 'end' : 'start')
                    } text-gray-800 fw-semibold fs-6`}
                    style={paddingStyle}
                  >
                    {col.isNumeric ? (
                      <div className="d-flex justify-content-end align-items-center">
                        {/* Numeric value: always right-aligned */}
                        <div
                          className={'fw-bold text-end'}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth:
                              col.showGrowthArrow && isGrowthObject(growthVal)
                                ? 'calc(100% - 90px)'
                                : '100%'
                          }}
                        >
                          {typeof currentValue === 'number' ? (
                            <>
                              <LomavisShortenedNumber
                                className={''}
                                value={currentValue}
                                average={col.showAbbreviation}
                              />
                              {col.showPercentage && '%'}
                            </>
                          ) : (
                            currentValue
                          )}
                        </div>

                        {/* Growth badge: fixed position */}
                        {col.showGrowthArrow && isGrowthObject(growthVal) && (
                          <div
                            className={'d-flex justify-content-end fw-bold'}
                            style={{
                              width: '80px',
                              marginLeft: '10px',
                              flexShrink: 0
                            }}
                          >
                            <LomavisGrowthBadge2
                              before={growthVal.previous}
                              after={growthVal.current}
                              blur={col.blurGrowthIndicator}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>{currentValue}</>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        {showSumRow && (
          <tfoot className="fs-1 fw-bolder border-top">
            <tr>
              {columns.map((col, colIndex) => {
                const isNumeric = col.isNumeric;
                const isPercentage = col.showPercentage || false;
                let sum = calculateSum(col.key, isPercentage);
                // Round percentage values to 2 decimal places
                if (isPercentage) {
                  sum = Math.round(sum * 100) / 100;
                }
                // Add padding to first and last cells
                const isFirstCell = colIndex === 0;
                const isLastCell = colIndex === columns.length - 1;
                const paddingStyle = {
                  minWidth: isNumeric ? '120px' : 'auto',
                  paddingLeft: isFirstCell ? '1rem' : undefined,
                  paddingRight: isLastCell ? '1rem' : undefined
                };

                return (
                  <td
                    key={col.key}
                    className={`text-${
                      col.align ?? (isNumeric ? 'end' : 'start')
                    }`}
                    style={paddingStyle}
                  >
                    {colIndex === 0 ? (
                      t('Total')
                    ) : isNumeric ? (
                      <div className="d-flex justify-content-end align-items-center">
                        {/* Numeric value: always right-aligned */}
                        <div
                          className={'d-flex flex-row fw-bold text-end'}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth:
                              col.showGrowthArrow &&
                              hasColumnGrowthData(col.key)
                                ? 'calc(100% - 90px)'
                                : '100%'
                          }}
                        >
                          <>
                            {isPercentage && (
                              <span
                                className={
                                  'small d-flex align-self-end fs-5 fw-normal text-muted'
                                }
                              >
                                Ø&nbsp;
                              </span>
                            )}
                            <LomavisShortenedNumber
                              value={sum}
                              compactLength={isPercentage ? 2 : 3}
                              average={col.showAbbreviation}
                            />
                            {isPercentage && (
                              <span style={{ fontWeight: 'normal' }}>
                                &nbsp;%
                              </span>
                            )}
                          </>
                        </div>
                        {/* Empty div to maintain alignment with growth badges above - only shown if growth data is present in this column */}
                        {col.showGrowthArrow &&
                          hasColumnGrowthData(col.key) && (
                            <div
                              className={'d-flex justify-content-end fw-bold'}
                              style={{
                                width: '80px',
                                marginLeft: '10px',
                                flexShrink: 0
                              }}
                            ></div>
                          )}
                      </div>
                    ) : (
                      ''
                    )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};
